import React, { useEffect } from 'react';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import { Button } from '@mui/base/Button';
import { styled } from '@mui/system';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import {
  RED, GRAY_DARK, GRAY_VERY_LIGHT, GREEN_BKG_OPT_SELECTED,
  GREEN_DARK, GREEN_TEXT_OPT_SELECTED, DISABLED_BKG,
} from '../../../assets/Styles';
import './index.css'
import { itemSelection } from '../../../models/itemSelection.model';


interface AutocompleteProps {
  options: any
  label: string
  value: any
  name: string
  handleSelection?: (k: any, v: any) => void
  valueOnClear?: any
  placeholder?: string
  disabled?: boolean
  required?: boolean
  textColor?: string
  readOnly?: boolean
  error?: boolean
  helperText?: string
}

const Autocomplete: React.FC<AutocompleteProps> = ({ options, label, value, name,
  handleSelection, valueOnClear, placeholder, disabled, required, textColor,
  readOnly, error, helperText }) => {
  return <AutocompleteForward
    options={options}
    label={label}
    value={value}
    name={name}
    handleSelection={handleSelection}
    valueOnClear={valueOnClear}
    placeholder={placeholder}
    disabled={disabled}
    required={required}
    textColor={textColor}
    readOnly={readOnly}
    error={error}
    helperText={helperText}
  />;
}

const AutocompleteForward = React.forwardRef(function Autocomplete(
  props: any,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    label,
    name,
    value,
    options,
    handleSelection,
    valueOnClear,
    placeholder,
    bgColor = '#f9fdfe',
    disableClearable = false,
    disabled = false,
    required = false,
    readOnly = false,
    error,
    helperText = '',
    ...other
  } = props;

  const [selected, setSelected] = React.useState<itemSelection | null>(value ?? null);

  useEffect(() => {
    const selectedItem = (value && value.id !== '') ? value : null
    setSelected(selectedItem)
  }, [value])

  useEffect(() => {
    if (options.length === 1 && required) handleChange(options[0])
  }, [options.length])

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getPopupIndicatorProps,
    getClearProps,
    getListboxProps,
    getOptionProps,
    id,
    popupOpen,
    setAnchorEl,
    groupedOptions,
  } = useAutocomplete({
    ...props,
    value: selected,
    onChange: (event, newValue: any) => handleChange(newValue),
    componentName: 'BaseAutocompleteIntroduction',
  });

  const handleChange = (newValue: itemSelection) => {
    if (newValue != null) {
      setSelected(newValue)
      handleSelection(name, newValue)
    }
  }

  const handleClear = () => {
    let newValue = valueOnClear ? valueOnClear : undefined
    setSelected(newValue)
    handleSelection(name, newValue ? newValue : '')
  }

  const hasClearIcon = !disableClearable && !disabled && !readOnly && selected && selected.label;
  const labelText = `${label} ${required ? " * " : ""}`

  const rootRef = useForkRef(ref, setAnchorEl);

  return (
    <div className='autocomplete-box'>

      <Label className={required && !selected && !helperText ? 'required' : undefined}
        {...getInputLabelProps()}>
        {labelText}
        {(error) && (!selected?.id) && (
          <span className="autocomplete__error-message">
            {helperText}
          </span>
        )}
      </Label>

      <StyledAutocompleteRoot
        {...getRootProps(other)}
        ref={rootRef}
        className={error ? 'error-required' : undefined}
        sx={{ backgroundColor: `${disabled ? DISABLED_BKG : bgColor}` }}
      >
        <input
          className='autocomplete-input'
          placeholder={placeholder}
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          required
          {...getInputProps()}
        />
        {hasClearIcon && (
          <StyledClearIndicator {...getClearProps()}>
            <ClearIcon onClick={handleClear} />
          </StyledClearIndicator>
        )}
        {(!disabled) && (
          <StyledPopupIndicator
            {...getPopupIndicatorProps()}
            className={popupOpen ? 'popupOpen' : undefined}
          >
            <ArrowDropDownIcon />
          </StyledPopupIndicator>
        )}
      </StyledAutocompleteRoot>

      {popupOpen ? (
        <div className='autocomplete-popup'>
          <StyledListbox {...getListboxProps()}>
            {(groupedOptions).map((option: any, index) => {
              const optionProps = getOptionProps({ option, index });
              const { key, ...restOptionProps } = optionProps as { key: React.Key };

              return <StyledOption key={key} {...restOptionProps}>{option.label}</StyledOption>;
            })}

            {groupedOptions.length === 0 && (
              <StyledNoOptions>Sin Opciones</StyledNoOptions>
            )}
          </StyledListbox>
        </div>
      ) : null}
    </div>
  );
});

const Label = styled("label")(
  ({ theme }) => `
    font-size: 12px;
    display: block;
    font-weight: 400;
    color: ${GRAY_DARK};

    &.required {
      color: ${RED};
    }
    `
);

const StyledAutocompleteRoot = styled('div')(
  ({ theme }) => `
  display: flex;
  flex: 1;
  font-weight: 400;
  border-radius: 6px;
  color: ${GRAY_DARK};
  border: 1px solid ${GREEN_DARK};
  gap: 1px;
  padding-right: 5px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  &.error-required {
    border: 1px solid red;
  }

  &:hover {
    border: 1px solid ${GREEN_DARK};
    box-shadow: 0 0 0 1px ${GREEN_DARK};
  }

  &:focus {
    border: 1.5px solid ${GREEN_DARK};
    box-shadow: 0 0 0 1.5px ${GREEN_DARK};

  // firefox
  &:focus-visible {
    outline: 0;
  }

  }
`,
);

const StyledListbox = styled('ul')(
  ({ theme }) => `
  font-size: 14px;
  box-sizing: border-box;
  padding: 6px;
  margin: 8px 0;
  width: auto;
  border-radius: 6px;
  overflow: auto;
  outline: 0px;
  max-height: 300px;
  z-index: 10000;
  background: ${'white'};
  border: 1px solid ${GREEN_DARK};
  color: ${GRAY_DARK};
  };
  `,
);

const StyledOption = styled('li')(
  ({ theme }) => `
  list-style: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  z-index: 10000;

  &:last-of-type {
    border-bottom: none;
  }

  &[aria-selected=true] {
    background-color: ${GREEN_BKG_OPT_SELECTED};
    color: ${GREEN_DARK};
  }

  &.Mui-focused,
  &.Mui-focusVisible {
    background-color: ${GRAY_VERY_LIGHT};
    color: ${GRAY_DARK};
  }

  &[aria-selected=true].Mui-focused,
  &[aria-selected=true].Mui-focusVisible {
    background-color: ${GREEN_BKG_OPT_SELECTED};
    color: ${GREEN_DARK};
  }
  `,
);

const StyledPopupIndicator = styled(Button)(
  ({ theme }) => `
    outline: 0;
    box-shadow: none;
    border: 0;
    border-radius: 6px;
    background-color: transparent;
    align-self: center;
    padding: 0 2px;

    &:hover {
      background-color: ${GREEN_TEXT_OPT_SELECTED};
      cursor: pointer;
    }


    &.popupOpen > svg {
      transform: translateY(2px) rotate(180deg);
    }
  `,
);

const StyledClearIndicator = styled(Button)(
  ({ theme }) => `
    outline: 0;
    box-shadow: none;
    border: 0;
    border-radius: 6px;
    background-color: transparent;
    align-self: center;
    padding: 0px 2px;

    &:hover {
      background-color: ${GREEN_TEXT_OPT_SELECTED};
      cursor: pointer;
    }

    & > svg {
      transform: translateY(2px) scale(0.9);
    }
  `,
);

const StyledNoOptions = styled('li')`
  list-style: none;
  padding: 8px;
  cursor: default;
`;

export default Autocomplete;